import { useQuery } from '@tanstack/react-query';
import { type CategoryTreeResponse } from '@aps/contracts';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  Banner,
  BrandLogo,
  Button,
  EmptyState,
  GarageCard,
  ProductCard,
  ActiveOrderCard,
  CategoryIcon,
  CategoryVisual,
  IconMore,
  IconSearch,
  SearchBar,
  Skeleton,
  Tile,
  pickText,
} from '@aps/ui';
import { useNavigation } from '../app/navigation.js';
import { useSettings } from '../app/settings.js';
import { canOrder } from '../app/session.js';
import { api, publicMediaUrl } from '../lib/api.js';
import { bannerRoute } from '../lib/banner-link.js';
import { useAddToCart } from '../lib/use-add-to-cart.js';

const BANNER_AUTOPLAY_MS = 5_000;

/** Bosh sahifa plitkalari: 4 ustun x 2 qator (maket §2.4). */
const HOME_TILE_SLOTS = 8;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Bosh sahifa plitkalari: bo'sh bo'lmagan kategoriyalar (`productCount !== 0`,
 * tartib serverdan). 8 tagacha — hammasi; undan ko'p bo'lsa — dastlabki 7 ta
 * va 8-o'rinda "Boshqalar" (barcha kategoriyalar ekrani).
 */
export function pickHomeCategories(all: readonly CategoryTreeResponse[]): {
  shown: readonly CategoryTreeResponse[];
  hasMore: boolean;
} {
  const visible = all.filter((category) => category.productCount !== 0);
  if (visible.length <= HOME_TILE_SLOTS) return { shown: visible, hasMore: false };
  return { shown: visible.slice(0, HOME_TILE_SLOTS - 1), hasMore: true };
}

export interface BannerCarouselProps {
  items: readonly ReactNode[];
}

/**
 * Native gorizontal scroll saqlanadi: Telegram WebView touch inertiyasini
 * qo'lda qayta yasash undan yomonroq ishlaydi. JS faqat joriy kartani
 * kuzatadi va user boshqarmayotgan paytda navbatdagisiga o'tadi.
 */
export function BannerCarousel({ items }: BannerCarouselProps): ReactNode {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isInteracting, setIsInteracting] = useState(false);
  const [reduceMotion] = useState(prefersReducedMotion);
  const hasMultipleItems = items.length > 1;

  const moveTo = useCallback(
    (index: number): void => {
      const scroller = scrollerRef.current;
      const first = scroller?.firstElementChild;
      const target = scroller?.children.item(index);
      if (!(scroller instanceof HTMLElement) || !(target instanceof HTMLElement)) return;

      const origin = first instanceof HTMLElement ? first.offsetLeft : 0;
      scroller.scrollTo({
        left: target.offsetLeft - origin,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
      setActiveIndex(index);
    },
    [reduceMotion],
  );

  useEffect(() => {
    if (!hasMultipleItems || isInteracting || reduceMotion) return undefined;

    const timer = window.setTimeout(() => {
      moveTo((activeIndex + 1) % items.length);
    }, BANNER_AUTOPLAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [activeIndex, hasMultipleItems, isInteracting, items.length, moveTo, reduceMotion]);

  const syncActiveItem = (): void => {
    const scroller = scrollerRef.current;
    if (scroller === null) return;

    const cards = Array.from(scroller.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );
    const origin = cards[0]?.offsetLeft ?? 0;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    cards.forEach((card, index) => {
      const distance = Math.abs(card.offsetLeft - origin - scroller.scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    setActiveIndex(closestIndex);
  };

  return (
    <div className="aps-banner-carousel">
      <div
        ref={scrollerRef}
        className="aps-banners"
        onPointerDown={() => {
          setIsInteracting(true);
        }}
        onPointerUp={() => {
          setIsInteracting(false);
        }}
        onPointerCancel={() => {
          setIsInteracting(false);
        }}
        onScroll={syncActiveItem}
      >
        {items}
      </div>
      {hasMultipleItems && (
        <div
          className="aps-banner-carousel__status"
          role="img"
          aria-label={`${String(activeIndex + 1)} / ${String(items.length)}`}
        >
          {items.map((_, index) => (
            <span
              className="aps-banner-carousel__dot"
              data-active={index === activeIndex ? 'true' : 'false'}
              aria-hidden="true"
              key={index}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Asosiy ekran.
 *
 * Ekran faylida BITTA HAM yangi CSS qoidasi yozilmaydi — faqat dizayn
 * tizimidagi klasslar va komponentlar (docs/03-UI-UX.md).
 */
export function HomeScreen(): ReactNode {
  const { t, locale } = useSettings();
  const { push, switchRoot } = useNavigation();
  const cart = useAddToCart();

  const categories = useQuery({ queryKey: ['categories'], queryFn: api.categories });
  const popular = useQuery({
    queryKey: ['products', 'popular'],
    queryFn: () => api.products({ limit: 6, sort: 'popular' }),
  });
  const garage = useQuery({
    queryKey: ['garage'],
    queryFn: api.garage,
    retry: false,
    enabled: canOrder(),
  });
  const banners = useQuery({ queryKey: ['banners'], queryFn: api.banners });
  const recommended = useQuery({
    queryKey: ['recommendations'],
    queryFn: api.recommendations,
    retry: false,
    enabled: canOrder(),
  });
  const orders = useQuery({
    queryKey: ['orders'],
    queryFn: () => api.orders(),
    retry: false,
    enabled: canOrder(),
  });

  /*
   * Faol buyurtma — YAKUNLANMAGAN eng yangisi.
   *
   * Bosh ekranda BITTASI ko'rsatiladi: mijozning ochiq buyurtmalari
   * ko'p bo'lsa ham, bosh ekran ularning ro'yxati emas. Hammasi
   * "Buyurtmalar" bo'limida.
   */
  const activeOrder =
    orders.data?.items.find(
      (order) => order.publicStage !== 'DELIVERED' && order.publicStage !== 'CANCELLED',
    ) ?? null;

  const homeCategories = pickHomeCategories(categories.data ?? []);

  // Banner bosilganda tegishli ekranga: tovar / kategoriya / katalog / qidiruv.
  const bannerTarget = (linkUrl: string | null) =>
    bannerRoute(linkUrl, categories.data ?? [], locale);
  const openBanner = (linkUrl: string | null): void => {
    const route = bannerTarget(linkUrl);
    if (route === null) return;
    // Katalog — pastki navigatsiyadagi ildiz ekran: stek tozalanadi.
    if (route.name === 'catalog' && route.categoryId === undefined) switchRoot('catalog');
    else push(route);
  };

  // Faol avtomobil bo'lmasa kartochka "qo'shing" holatida chiqadi.
  // Ilgari bu yerga bo'sh `makeName` va `year={0}` uzatilardi va ekranda
  // "Mashinangizni qo'shing 0" degan matn chiqib turardi.
  const activeVehicle = garage.data?.find((vehicle) => vehicle.isActive) ?? null;

  return (
    <div className="aps-screen">
      <header className="aps-appbar">
        {/*
          Qo'ng'iroqcha ataylab YO'Q: ilovada bildirishnomalar ekrani yo'q (buyurtma xabarlari
          Telegram chatiga keladi), bosilmaydigan "tugma" esa ishonchni buzadi.
        */}
        <BrandLogo />
      </header>

      <SearchBar
        leading={<IconSearch size={18} />}
        value=""
        onValueChange={(next) => {
          if (next.length > 0) push({ name: 'search', query: next });
        }}
        placeholder={t('home.searchPlaceholder')}
        readOnly
        onFocus={() => {
          push({ name: 'search' });
        }}
      />

      {/*
        Bannerlar SERVERDAN keladi va ko'rsatish oynasi ham u yerda
        tekshiriladi. Ilgari `Banner` komponenti import qilingan-u,
        hech qachon chizilmagan edi.
      */}
      {(banners.data ?? []).length > 0 && (
        <BannerCarousel
          items={(banners.data ?? []).map((banner, index) => (
            <Banner
              key={banner.id}
              priority={index === 0}
              ctaLabel={t('home.bannerView')}
              {...(bannerTarget(banner.linkUrl) === null
                ? {}
                : {
                    onSelect: () => {
                      openBanner(banner.linkUrl);
                    },
                  })}
              {...(banner.imageUrl === null ? {} : { imageUrl: publicMediaUrl(banner.imageUrl) })}
              {...(banner.colorKey === null
                ? {}
                : { icon: <CategoryIcon iconKey={banner.colorKey} size={84} /> })}
              title={pickText(banner.titleUz, banner.titleRu, locale)}
              {...(banner.subtitleUz === null
                ? {}
                : { subtitle: pickText(banner.subtitleUz, banner.subtitleRu, locale) })}
              {...(banner.badgeUz === null
                ? {}
                : { badge: pickText(banner.badgeUz, banner.badgeRu, locale) })}
              {...(banner.colorKey === null ? {} : { categoryKey: banner.colorKey })}
            />
          ))}
        />
      )}

      {activeOrder !== null && (
        <section className="aps-stack">
          <h2 className="aps-section-title">{t('home.activeOrder')}</h2>
          <ActiveOrderCard
            item={activeOrder}
            locale={locale}
            onSelect={() => {
              push({ name: 'order', id: activeOrder.id });
            }}
          />
        </section>
      )}

      <GarageCard
        vehicle={
          activeVehicle === null
            ? null
            : {
                makeName: activeVehicle.makeName,
                modelName: activeVehicle.modelName,
                year: activeVehicle.year,
              }
        }
        emptyTitle={t('home.garageEmpty')}
        emptyHint={t('home.garageHint')}
        locale={locale}
        onSelect={() => {
          switchRoot('profile');
        }}
      />

      {/* Maketda bo'lim sarlavhasi yo'q — nom faqat ekran o'quvchi uchun. */}
      <section className="aps-stack" aria-label={t('home.categories')}>
        {categories.isPending ? (
          <div className="aps-grid-4">
            {Array.from({ length: HOME_TILE_SLOTS }, (_, index) => (
              <Skeleton key={index} height="102px" />
            ))}
          </div>
        ) : categories.isError ? (
          <div role="alert">
            <EmptyState
              title={t('common.error')}
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    void categories.refetch();
                  }}
                >
                  {t('common.retry')}
                </Button>
              }
            />
          </div>
        ) : (
          <div className="aps-grid-4">
            {homeCategories.shown.map((category) => (
              <Tile
                key={category.id}
                categoryKey={category.iconKey ?? 'access'}
                icon={
                  <CategoryVisual
                    iconKey={category.iconKey}
                    imageUrl={publicMediaUrl(category.imageUrl)}
                    size={40}
                  />
                }
                label={locale === 'RU' ? (category.nameRu ?? category.nameUz) : category.nameUz}
                onSelect={() => {
                  push({
                    name: 'catalog',
                    categoryId: category.id,
                    title: category.nameUz,
                  });
                }}
              />
            ))}
            {homeCategories.hasMore && (
              // 8-plitka: qolgan kategoriyalar "Barcha kategoriyalar" ekranida (tovar emas).
              <Tile
                categoryKey="access"
                icon={<IconMore size={40} />}
                label={t('home.otherCategories')}
                onSelect={() => {
                  push({ name: 'categories' });
                }}
              />
            )}
          </div>
        )}
      </section>

      {/*
        Garajga asoslangan tavsiya.
        
        Blok FAQAT tovar bo'lganda chiziladi. Server kam natijali
        javobni bo'sh qaytaradi: "sizning mashinangiz uchun" sarlavhasi
        ostida bitta tovar turishi "bizda sizning mashinangizga hech
        narsa yo'q" degan ma'no beradi — bo'sh joydan yomonroq.
      */}
      {(recommended.data?.items.length ?? 0) > 0 && (
        <section className="aps-stack">
          <h2 className="aps-section-title">
            {t('home.forYourCar')} · {recommended.data?.vehicleLabel ?? ''}
          </h2>
          <div className="aps-grid-2">
            {(recommended.data?.items ?? []).map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                locale={locale}
                fallbackIcon={<CategoryIcon iconKey={product.categoryIconKey} size={50} />}
                onAddToCart={cart.add}
                addToCartLabel={t('card.addToCart')}
                addToCartDisabled={cart.disabled}
                onSelect={() => {
                  push({ name: 'product', slug: product.slug });
                }}
              />
            ))}
          </div>
        </section>
      )}

      <section className="aps-stack">
        <div className="aps-section-head">
          <h2 className="aps-section-head__title">{t('home.popular')}</h2>
          <button
            type="button"
            className="aps-section-head__link"
            onClick={() => {
              switchRoot('catalog');
            }}
          >
            {t('home.viewAll')}
            <span aria-hidden="true">→</span>
          </button>
        </div>
        {popular.isPending ? (
          <div className="aps-grid-2">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} height="220px" />
            ))}
          </div>
        ) : popular.isError ? (
          <div role="alert">
            <EmptyState
              title={t('common.error')}
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    void popular.refetch();
                  }}
                >
                  {t('common.retry')}
                </Button>
              }
            />
          </div>
        ) : popular.data.items.length === 0 ? (
          <EmptyState title={t('catalog.empty')} />
        ) : (
          <div className="aps-grid-2">
            {popular.data.items.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                locale={locale}
                fallbackIcon={<CategoryIcon iconKey={product.categoryIconKey} size={50} />}
                onAddToCart={cart.add}
                addToCartLabel={t('card.addToCart')}
                addToCartDisabled={cart.disabled}
                onSelect={() => {
                  push({ name: 'product', slug: product.slug });
                }}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
